import { useEffect, useMemo } from 'react';

import {
  useStreamRepositoryInjector,
  useBillingRepositoryInjector,
  usePointRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import { useAccountCompanyCountry, useAccountCompanyId, useAccountCompanyIsBillingEnabled } from 'application/hooks';

import useUserFeaturesOnboardingPass from 'domain/media/useUserFeaturesOnboardingPass';
import useUserFeaturesOnboarding from 'domain/media/useUserFeaturesOnboarding';
import { Country, UserFeatures } from 'domain/api/graphql/generated';

import { TPlayerTypeItem, UseSelectPlayerModalViewModel } from './types';
import { PLAYER_TEXT_TYPE } from './consts';
import { PlayerTypesInFlow } from '../types';

interface UseSelectPlayerModalViewModelProps {
  pointHasVideoSupport?: boolean;
}

const useSelectPlayerModalViewModel = ({
  pointHasVideoSupport,
}: UseSelectPlayerModalViewModelProps): UseSelectPlayerModalViewModel => {
  const {
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isSavedStreamsLoading,
    changeStream,
    copyTemplateStream,
    getSavedStreams,
  } = useStreamRepositoryInjector();

  const { getBillingInfo } = useBillingRepositoryInjector();
  const { pointSetDevice } = usePointRepositoryInjector();

  const companyId = useAccountCompanyId();
  const companyCountry = useAccountCompanyCountry();
  const isBillingEnabled = useAccountCompanyIsBillingEnabled();

  const { request: onboardingPassedRequest, result } = useUserFeaturesOnboardingPass();
  const { refetch: refetchFeaturesOnboarding } = useUserFeaturesOnboarding({
    features: [UserFeatures.WarningStreamFirstStart],
  });

  useEffect(() => {
    if (isBillingEnabled) {
      getBillingInfo(companyId);
    }
  }, []);

  const playersData = useMemo(() => {
    const players = [] as TPlayerTypeItem[];

    for (const type in PlayerTypesInFlow) {
      if (Object.prototype.hasOwnProperty.call(PlayerTypesInFlow, type)) {
        const playerType = type as PlayerTypesInFlow;

        const hideDevice =
          PLAYER_TEXT_TYPE[playerType].ruByOnly && !(companyCountry === Country.Ru || companyCountry === Country.By);

        const isHideVideoDevice = playerType === PlayerTypesInFlow.VIDEOPLAYER && !pointHasVideoSupport;

        if (!hideDevice && !isHideVideoDevice) {
          players.push({
            title: PLAYER_TEXT_TYPE[playerType].title,
            description: PLAYER_TEXT_TYPE[playerType].description,
            descriptionFallback: PLAYER_TEXT_TYPE[playerType].descriptionFallback,
            type: playerType,
            imageSrc: PLAYER_TEXT_TYPE[playerType].imageSrc,
            imageSrcFallback: PLAYER_TEXT_TYPE[playerType].imageSrcFallback,
          });
        }
      }
    }

    return players;
  }, []);

  const videoPlayersData = [
    {
      title: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].title,
      description: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].description,
      descriptionFallback: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].descriptionFallback,
      type: PlayerTypesInFlow.MOBILE,
      imageSrc: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].imageSrc,
    },
    {
      title: PLAYER_TEXT_TYPE[PlayerTypesInFlow.VIDEOPLAYER].title,
      description: PLAYER_TEXT_TYPE[PlayerTypesInFlow.VIDEOPLAYER].description,
      type: PlayerTypesInFlow.VIDEOPLAYER,
      imageSrc: PLAYER_TEXT_TYPE[PlayerTypesInFlow.VIDEOPLAYER].imageSrc,
    },
  ];

  return {
    playersData,
    videoPlayersData,
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isSavedStreamsLoading,
    isUserFeaturesOnboardingPassLoading: result.loading,
    changeStream,
    copyTemplateStream,
    getSavedStreams,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    pointSetDevice,
  };
};

export default useSelectPlayerModalViewModel;
